import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, MessageCircle, ShieldCheck, Wrench, Headphones, Sparkles,
  TrendingUp, HeartHandshake, BarChart3, Building2, Quote, Check
} from "lucide-react";
import api, { COMPANY, IMG } from "@/lib/api";
import { useImage } from "@/context/MediaContext";
import { Section, PrimaryButton, OutlineButton, GlassCard, Counter, fadeUp } from "@/components/ui-kit";

function Hero() {
  const heroImg = useImage("hero", IMG.hero);
  return (
    <section className="relative min-h-[92vh] flex items-end overflow-hidden">
      {/* Background image */}
      <img
        src={heroImg}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover opacity-70"
      />
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/30" />
      <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_100%,rgba(59,130,246,0.18),transparent_60%)]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-32 pt-32 w-full">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-xs tracking-[0.3em] uppercase font-semibold text-[#3B82F6] mb-6"
          data-testid="hero-eyebrow"
        >
          {COMPANY.primary_message}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.05 }}
          className="font-display font-semibold tracking-tighter text-5xl md:text-7xl lg:text-[88px] leading-[0.95] max-w-5xl"
          data-testid="hero-title"
        >
          Building India's most<br/>
          <span className="text-[#D1D5DB]">driver-focused</span> fleet.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-8 text-lg md:text-xl text-[#D1D5DB] max-w-2xl leading-relaxed"
          data-testid="hero-sub"
        >
          GK Fleet provides professionally managed vehicles to ambitious drivers, helping them earn consistently while we handle fleet operations, support, and vehicle management.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-10 flex flex-col sm:flex-row gap-3"
        >
          <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp">
            <PrimaryButton><MessageCircle className="w-4 h-4" /> WhatsApp Us</PrimaryButton>
          </a>
          <Link to="/drivers" data-testid="hero-cta-driver">
            <OutlineButton>Become a Driver <ArrowUpRight className="w-4 h-4" /></OutlineButton>
          </Link>
        </motion.div>
      </div>

      {/* Down indicator */}
      <div className="absolute bottom-6 right-8 hidden md:flex items-center gap-3 text-[#9CA3AF] text-xs tracking-[0.2em] uppercase">
        <span>Scroll</span>
        <span className="w-12 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
      </div>
    </section>
  );
}

function TrustBar({ stats }) {
  const items = [
    { value: stats?.vehicles_current ?? 7, suffix: "", label: "Vehicles in operation", testId: "trust-vehicles" },
    { value: stats?.uptime_percent ?? 75, suffix: "%", label: "Operational uptime", testId: "trust-uptime" },
    { value: 1, prefix: "", suffix: "", label: stats?.city ?? "Hyderabad", textOverride: stats?.city ?? "Hyderabad", testId: "trust-city" },
    { value: stats?.target_vehicles ?? 100, suffix: "+", label: `Goal by ${stats?.target_year ?? 2030}`, testId: "trust-goal" },
  ];
  return (
    <section className="relative -mt-px border-y border-white/10 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
        {items.map((it, i) => (
          <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} className="flex flex-col gap-2">
            <div className="text-4xl md:text-5xl font-display font-semibold text-white">
              {it.textOverride ? <span data-testid={it.testId}>{it.textOverride}</span> : <Counter testId={it.testId} to={it.value} suffix={it.suffix || ""} />}
            </div>
            <div className="text-xs tracking-[0.18em] uppercase text-[#9CA3AF]">{it.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function WhyExists() {
  const driverImg = useImage("driver_section", IMG.driver);
  return (
    <Section eyebrow="Why GK Fleet exists" title="Built around drivers, not just vehicles.">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <motion.div {...fadeUp} className="lg:col-span-7 space-y-6 text-lg leading-relaxed text-[#D1D5DB]">
          <p>Too many fleet operators see drivers as numbers. <span className="text-white">GK Fleet was founded on a different belief.</span></p>
          <p>When drivers succeed, the business succeeds. We are building a company where drivers are respected, supported professionally, and given reliable vehicles that help them build sustainable income and long-term growth.</p>
          <div className="pt-4 grid grid-cols-2 gap-4 max-w-md">
            {["Driver-first culture","Transparent terms","Long-term thinking","Accountability"].map((t) => (
              <div key={t} className="flex items-center gap-2 text-sm text-[#D1D5DB]">
                <Check className="w-4 h-4 text-[#3B82F6]" /> {t}
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-5">
          <div className="relative rounded-2xl overflow-hidden border border-white/10">
            <img src={driverImg} alt="A GK Fleet driver partner" className="w-full h-[460px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
              <p className="text-xs tracking-[0.2em] uppercase text-[#3B82F6]">Driver Partner</p>
              <p className="font-display text-xl mt-1">Our partners are the company.</p>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

function DriversPartners() {
  const pillars = [
    { icon: ShieldCheck, t: "Reliable Vehicles", d: "Professionally maintained, ready to earn from day one." },
    { icon: Sparkles, t: "Transparent Rentals", d: "Clear terms, no hidden fees, no surprises." },
    { icon: Headphones, t: "Dedicated Support", d: "Real humans on call when something goes wrong." },
    { icon: Wrench, t: "Professional Operations", d: "Service scheduled proactively — your earnings never stop." },
    { icon: TrendingUp, t: "Long-Term Opportunities", d: "Built for partners who want to grow with us." },
    { icon: HeartHandshake, t: "Fair Treatment", d: "We measure success by how our drivers feel about us." },
  ];
  return (
    <Section eyebrow="Our drivers, our partners" title="What partnership actually means at GK Fleet." kicker="Six promises we make to every driver who joins the GK Fleet partner network.">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {pillars.map((p, i) => (
          <motion.div key={p.t} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}>
            <GlassCard className="h-full">
              <p.icon className="w-6 h-6 text-[#3B82F6]" />
              <h3 className="mt-5 font-display text-xl font-medium">{p.t}</h3>
              <p className="mt-2 text-[#9CA3AF] leading-relaxed">{p.d}</p>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", t: "Apply", d: "Submit a short application — name, phone, city, licence." },
    { n: "02", t: "Verification", d: "Our team verifies your driving record and documents." },
    { n: "03", t: "Vehicle Allocation", d: "Receive a professionally maintained WagonR, ready to earn." },
    { n: "04", t: "Start Earning", d: "Hit the road on Uber, Ola, or Rapido. We handle the rest." },
  ];
  return (
    <Section eyebrow="How it works" title="From application to first trip in days.">
      <div className="relative">
        <div className="hidden lg:block absolute top-12 left-[6%] right-[6%] h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.n} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} className="relative">
              <div className="relative z-10 w-6 h-6 rounded-full bg-[#3B82F6] mb-6 pulse-glow" />
              <p className="font-mono text-xs text-[#9CA3AF]">{s.n}</p>
              <h3 className="mt-2 font-display text-2xl">{s.t}</h3>
              <p className="mt-3 text-[#9CA3AF] leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
      <motion.div {...fadeUp} className="mt-14">
        <Link to="/drivers" data-testid="howitworks-cta">
          <PrimaryButton>Start your application <ArrowUpRight className="w-4 h-4" /></PrimaryButton>
        </Link>
      </motion.div>
    </Section>
  );
}

function WhyChoose() {
  const cards = [
    { t: "Reliable Vehicles", d: "Late-model compact cars maintained on a fixed service cadence." },
    { t: "Quick Support", d: "Direct lines to operations staff — not call-centers." },
    { t: "Transparent Terms", d: "Plain-language rental agreements, no fine print games." },
    { t: "Professional Management", d: "Insurance, service, paperwork — handled for you." },
    { t: "Growth Opportunities", d: "Top performers get priority on new vehicle inventory." },
    { t: "Driver-First Culture", d: "Every operating decision starts with: 'is this good for the driver?'" },
  ];
  return (
    <Section eyebrow="Why drivers choose GK Fleet" title="A fleet operator built like a tech company.">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((c, i) => (
          <motion.div key={c.t} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}>
            <GlassCard className="group h-full">
              <div className="flex items-start justify-between">
                <h3 className="font-display text-xl font-medium">{c.t}</h3>
                <span className="text-[#3B82F6] opacity-0 group-hover:opacity-100 transition"><ArrowUpRight className="w-5 h-5" /></span>
              </div>
              <p className="mt-3 text-[#9CA3AF] leading-relaxed">{c.d}</p>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function Roadmap({ roadmap }) {
  const items = roadmap?.length ? roadmap : [
    { year: 2025, vehicles: 7, title: "Foundation" },
    { year: 2026, vehicles: 14, title: "Scale" },
    { year: 2027, vehicles: 30, title: "Expansion" },
    { year: 2030, vehicles: 100, title: "Vision" },
  ];
  const max = Math.max(...items.map((i) => i.vehicles));
  return (
    <Section eyebrow="Growth roadmap" title="From 7 vehicles today to 100 by 2030." kicker="Each milestone is a learning loop — not a sprint.">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        {items.map((m, i) => {
          const pct = Math.round((m.vehicles / max) * 100);
          return (
            <motion.div key={m.year} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
              <GlassCard className="h-full" data-testid={`roadmap-${m.year}`}>
                <p className="font-mono text-xs text-[#3B82F6] tracking-[0.2em]">{m.year}</p>
                <p className="mt-3 font-display text-5xl font-semibold">{m.vehicles}</p>
                <p className="text-xs tracking-[0.18em] uppercase text-[#9CA3AF] mt-1">{m.title || "Vehicles"}</p>
                <div className="mt-6 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${pct}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.2 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full bg-gradient-to-r from-[#3B82F6] to-[#60A5FA]"
                  />
                </div>
                {m.description && <p className="mt-4 text-sm text-[#9CA3AF] leading-relaxed">{m.description}</p>}
              </GlassCard>
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
}

function StrategicPillars() {
  const techImg = useImage("tech_bg", IMG.network);
  const pillars = [
    { i: TrendingUp, t: "Fleet Expansion", d: "Deliberate vehicle acquisition aligned with driver demand." },
    { i: BarChart3, t: "Technology Integration", d: "Modern tooling for ops, telemetry, and driver experience." },
    { i: HeartHandshake, t: "Driver Success", d: "Programs that meaningfully improve driver income." },
    { i: ShieldCheck, t: "Operational Excellence", d: "Insurance, service, compliance — handled with rigor." },
    { i: Building2, t: "Long-Term Growth", d: "Building a fleet asset platform, not chasing trends." },
  ];
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <img src={techImg} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...fadeUp} className="max-w-3xl mb-14">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#3B82F6] mb-4">Building the future</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight font-semibold">Five strategic pillars shaping the next five years.</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {pillars.map((p, i) => (
            <motion.div key={p.t} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.07 }}>
              <div className="h-full rounded-2xl bg-[#1A1A1A]/80 border border-white/10 p-6">
                <p.i className="w-5 h-5 text-[#3B82F6]" />
                <h3 className="mt-5 font-display text-lg font-medium">{p.t}</h3>
                <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{p.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials({ items }) {
  const list = (items?.length ? items : []).filter((t) => t.is_published !== false);
  if (list.length === 0) return null;
  return (
    <Section eyebrow="Voices from the road" title="What our driver partners say.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {list.slice(0, 6).map((t, i) => (
          <motion.div key={t.id || i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.07 }}>
            <GlassCard className="h-full flex flex-col" data-testid={`testimonial-${i}`}>
              <Quote className="w-6 h-6 text-[#3B82F6] opacity-60" />
              <p className="mt-5 text-[#D1D5DB] leading-relaxed">"{t.quote}"</p>
              <div className="mt-6 pt-5 border-t border-white/10">
                <p className="font-medium text-white">{t.name}</p>
                <p className="text-xs text-[#9CA3AF]">{t.role}{t.city ? ` · ${t.city}` : ""}</p>
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function FinalCTA() {
  return (
    <section className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_50%_50%,rgba(59,130,246,0.12),transparent_70%)]" />
      <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
        <motion.h2 {...fadeUp} className="font-display text-4xl md:text-6xl tracking-tighter font-semibold leading-[1.05]">
          Ready to drive with GK Fleet?
        </motion.h2>
        <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="mt-5 text-lg text-[#D1D5DB]">
          Three ways to start the conversation. Pick what works for you.
        </motion.p>
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/drivers" data-testid="final-cta-apply"><PrimaryButton>Apply as Driver</PrimaryButton></Link>
          <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="final-cta-whatsapp"><OutlineButton><MessageCircle className="w-4 h-4" /> WhatsApp Us</OutlineButton></a>
          <Link to="/investors" data-testid="final-cta-partner"><OutlineButton>Partner With Us <ArrowUpRight className="w-4 h-4" /></OutlineButton></Link>
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  const [stats, setStats] = useState(null);
  const [roadmap, setRoadmap] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/fleet/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/roadmap").then((r) => setRoadmap(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />
      <TrustBar stats={stats} />
      <WhyExists />
      <DriversPartners />
      <HowItWorks />
      <WhyChoose />
      <Roadmap roadmap={roadmap} />
      <StrategicPillars />
      <Testimonials items={testimonials} />
      <FinalCTA />
    </div>
  );
}
